const CACHE_NAME = 'geobu-media-v1';

export type CacheProgressCallback = (completed: number, total: number) => void;

export class MediaCacheService {
  private cacheAvailable: boolean = typeof window !== 'undefined' && 'caches' in window;
  private memoryAudioBlobs: Map<string, string> = new Map();
  private pendingDownloads: Set<string> = new Set();

  async getCachedAudioUrl(url: string): Promise<string> {
    if (this.memoryAudioBlobs.has(url)) {
      return this.memoryAudioBlobs.get(url)!;
    }

    if (!this.cacheAvailable) return url;

    try {
      const cache = await caches.open(CACHE_NAME);
      const matched = await cache.match(url);
      if (matched) {
        const blob = await matched.blob();
        const objUrl = URL.createObjectURL(blob);
        this.memoryAudioBlobs.set(url, objUrl);
        return objUrl;
      }
    } catch (e) {
      console.warn('Cache read error:', e);
    }
    return url;
  }

  async cacheMediaUrl(url: string): Promise<void> {
    if (!url || !this.cacheAvailable || this.pendingDownloads.has(url)) return;

    try {
      const cache = await caches.open(CACHE_NAME);
      const existing = await cache.match(url);
      if (existing) return;

      this.pendingDownloads.add(url);
      const response = await fetch(url, { mode: 'cors' });
      if (response.ok) {
        await cache.put(url, response.clone());
        const blob = await response.blob();
        this.memoryAudioBlobs.set(url, URL.createObjectURL(blob));
      }
    } catch (err) {
      console.warn(`Failed to pre-cache media ${url}:`, err);
    } finally {
      this.pendingDownloads.delete(url);
    }
  }

  async prefetchMediaList(urls: string[], onProgress?: CacheProgressCallback): Promise<void> {
    const validUrls = urls.filter((u) => Boolean(u));
    if (validUrls.length === 0) return;

    let completed = 0;
    const total = validUrls.length;
    onProgress?.(completed, total);

    for (const url of validUrls) {
      await this.cacheMediaUrl(url);
      completed++;
      onProgress?.(completed, total);
    }
  }

  async clearCache(): Promise<void> {
    this.memoryAudioBlobs.forEach((objUrl) => URL.revokeObjectURL(objUrl));
    this.memoryAudioBlobs.clear();
    if (this.cacheAvailable) {
      await caches.delete(CACHE_NAME);
    }
  }
}

export const mediaCache = new MediaCacheService();
